import React, { useState, useEffect } from "react";
import Header from "../../Components/Header";
import { Link, useLocation } from "react-router-dom";
import { getBookingDetailById } from "../../Services/HotelSearchApi";

const ThankYou = () => {
  const location = useLocation();
  const refId = new URLSearchParams(location.search).get("refId");
  const { bookingRefId, confirmationNo } = location.state || {};

  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (refId) {
        try {
          let response = await getBookingDetailById(refId);
          if (Array.isArray(response) && response.length > 0)
            response = response[0];

          if (response && response.booking) {
            setBookingData(response.booking);
          } else if (response && response.Booking) {
            setBookingData(response.Booking);
          } else if (response && response.pkid) {
            setBookingData(response);
          } else {
            setError("Booking details not found.");
          }
        } catch (err) {
          console.error("Error retrieving booking details:", err);
          setError("Failed to fetch booking information.");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingRefId, confirmationNo]);

  const sectionHeaderStyle = {
    color: "#110942",
    fontWeight: "700",
    fontSize: "1.1rem",
    borderLeft: "4px solid #110942",
    paddingLeft: "15px",
    marginBottom: "20px",
  };

  const tableHeaderStyle = {
    background: "#f8f9fa",
    color: "#333",
    fontWeight: "600",
    fontSize: "0.85rem",
    borderBottom: "1px solid #dee2e6",
  };

  const tableCellStyle = {
    fontSize: "0.85rem",
    padding: "12px 15px",
    color: "#444",
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "80vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  if (error || !bookingData) {
    return (
      <>
        <Header />
        <div className="container py-5">
          <div className="alert alert-danger">
            {error || "Booking details not found."}
          </div>
          <Link to="/dashboard/mybooking" className="btn btn-primary">
            Go to My Bookings
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <section className="py-5" style={{ minHeight: "90vh" }}>
        <div className="container">
          <div className="card border-0 shadow-sm rounded-1 p-4 p-md-5 bg-white mx-auto">
            {/* Booking Confirmation Section */}
            <div className="mb-5">
              <h4 style={sectionHeaderStyle}>Booking Confirmation</h4>
              <div className="ps-3">
                <p className="mb-1 fw-bold text-dark">Dear Customer,</p>
                <p className="mb-1 text-dark">
                  Thank You. Your Booking Order is{" "}
                  {bookingData.bookingStatus === "SUCCESS" ||
                  bookingData.bookingStatus === "CONFIRMED"
                    ? "Confirmed Now"
                    : bookingData.bookingStatus}
                  .
                </p>
                <p className="mb-3 text-dark">
                  A confirmation email has been sent to your provided email
                  address.
                </p>
                <div className="d-flex align-items-center">
                  <p className="fw-bold mb-0" style={{ color: "#28a745" }}>
                    Reference No:{" "}
                    <span className="text-uppercase">
                      {bookingData.bookingRefId}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Trip Information Section */}
            <div className="mb-5">
              <h4 style={sectionHeaderStyle}>Hotel Information</h4>
              <div className="table-responsive">
                <table className="table table-bordered mb-0">
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Booking Status</th>
                      <th style={tableHeaderStyle}>Reference</th>
                      <th style={tableHeaderStyle}>Hotel Name</th>
                      <th style={tableHeaderStyle}>City</th>
                      <th style={tableHeaderStyle}>Check-In</th>
                      <th style={tableHeaderStyle}>Check-Out</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          ...tableCellStyle,
                          color: "#28a745",
                          fontWeight: "600",
                        }}
                      >
                        {bookingData.bookingStatus || "Confirmed"}
                      </td>
                      <td style={tableCellStyle}>
                        {bookingData.bookingRefId ||
                          bookingData.confirmationNo ||
                          "N/A"}
                      </td>
                      <td style={tableCellStyle}>
                        {bookingData.hotelName || "N/A"}
                      </td>
                      <td style={tableCellStyle}>
                        {bookingData.cityName || "N/A"}
                      </td>
                      <td style={tableCellStyle}>
                        {formatDate(bookingData.checkInDate)}
                      </td>
                      <td style={tableCellStyle}>
                        {formatDate(bookingData.checkOutDate)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Room Details & Passenger Information Section */}
            <div className="mb-5">
              <h4 style={sectionHeaderStyle}>Room & Guest Details</h4>
              {bookingData.rooms?.map((room, rIdx) => {
                const isRefundable = room.refundable || bookingData.rooms?.[0]?.refundable;
                return (
                  <div key={rIdx} className={rIdx > 0 ? "mt-4" : ""}>
                    <div
                      className="bg-light p-2 border border-bottom-0 fw-bold"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Room {rIdx + 1}: {room.roomName || room.roomId} (
                      {isRefundable ? "Refundable" : "Non-Refundable"})
                    </div>
                    <div className="table-responsive">
                      <table className="table table-bordered mb-0">
                        <thead>
                          <tr>
                            <th
                              className="text-center"
                              style={{ width: "80px", ...tableHeaderStyle }}
                            >
                              Sr.No
                            </th>
                            <th style={tableHeaderStyle}>Guest Name</th>
                            <th style={tableHeaderStyle}>Type</th>
                            <th style={tableHeaderStyle}>PAN / Passport</th>
                            <th style={tableHeaderStyle}>Primary</th>
                          </tr>
                        </thead>
                        <tbody>
                          {room.paxDetails?.map((pax, pIdx) => (
                            <tr key={pIdx}>
                              <td style={tableCellStyle} className="text-center">
                                {pIdx + 1}
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  textTransform: "capitalize",
                                }}
                              >
                                {pax.title} {pax.firstName} {pax.lastName}
                              </td>
                              <td style={tableCellStyle}>
                                {pax.paxType || "Adult"}
                              </td>
                              <td style={tableCellStyle}>
                                {pax.panNumber || pax.passportNo || "-"}
                              </td>
                              <td style={tableCellStyle}>
                                {pax.isPrimary || pax.isLeadGuest ? "Yes" : "No"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Payment Information Section */}
            <div className="mb-5">
              <h4 style={sectionHeaderStyle}>Payment Information</h4>
              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="d-flex flex-column gap-1">
                    <span
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        color: "#333",
                      }}
                    >
                      Payment Status
                    </span>
                    <span
                      style={{
                        color: "#28a745",
                        fontWeight: "600",
                        fontSize: "0.9rem",
                      }}
                    >
                      {bookingData.bookingStatus === "SUCCESS"
                        ? "Success"
                        : bookingData.bookingStatus}
                    </span>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="d-flex flex-column gap-1">
                    <span
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        color: "#333",
                      }}
                    >
                      Payment Amount
                    </span>
                    <span
                      style={{
                        fontWeight: "700",
                        fontSize: "1.1rem",
                        color: "#110942",
                      }}
                    >
                      {bookingData.currency}{" "}
                      {bookingData.totalPrice?.toLocaleString() || "0"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-start mt-4 pt-3">
              <Link
                to={`/Reservations/hotel/TransactionsDetails?referenceno=${bookingData.bookingRefId}`}
                className="btn text-white px-4 py-2 rounded-1"
                style={{
                  background: "#110942",
                  fontWeight: "600",
                  fontSize: "0.9rem",
                }}
              >
                Booking Detail
              </Link>
              <Link
                to="/"
                className="btn btn-outline-secondary px-4 py-2 rounded-1 ms-3"
                style={{ fontWeight: "600", fontSize: "0.9rem" }}
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ThankYou;
